/**
 * @file StrategySelection.jsx
 * @description Handles investment strategy selection and customization during onboarding
 *
 * @section Responsibilities
 * - Present available investment strategies
 * - Match strategies to user's risk profile
 * - Allow strategy customization
 * - Show performance metrics and comparisons
 * - Handle strategy selection confirmation
 * - Manage strategy allocation preferences
 *
 * @section Dependencies
 * - RiskProfile: User's risk assessment results
 * - StrategyData: Available investment strategies
 * - PerformanceMetrics: Strategy performance data
 * - AllocationService: Portfolio allocation management
 *
 * @section Integration Points
 * - Strategy API: Strategy information and data
 * - Risk Assessment: Risk profile matching
 * - Portfolio Service: Strategy allocation
 * - Onboarding Flow: Progress tracking
 *
 * @section State Management
 * - selectedStrategy: Currently selected strategy
 * - allocation: Strategy allocation preferences
 * - comparisonData: Strategy performance comparison
 * - customization: Strategy customization options
 *
 * @section Events
 * - onStrategySelect: Strategy selection
 * - onAllocationChange: Allocation updates
 * - onCustomization: Strategy customization
 * - onConfirm: Selection confirmation
 *
 * @section Props
 * @prop {Object} riskProfile - User's risk assessment results
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialSelection - Previously selected strategy
 */

import axios from "axios";
import React, { useState, useEffect } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/strategy-selection.css";
import DgButton from "../../components/DgButton";
import { useNavigate } from "react-router-dom";
import BallsBackground from "../../components/BallsBackground";

function useElementSize(element) {
  const [elementSize, setElementSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        width: element.current ? element.current.offsetWidth : 0,
        height: element.current ? element.current.offsetHeight : 0,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return elementSize;
}

function StrategySelection() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.session);

  const [data, setData] = useState({
    strategy: "Bull",
  });

  const updateStrategy = (stg) => {
    setData({ strategy: stg });
  };

  const updateUser = () => {
    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_BASE_URL}/user`, {
        PK: user.PK,
        SK: user.SK,
        ...data,
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  return (
    <div className="strategy-selection">
      <BallsBackground>
        <div className="content container shadow1">
          <div className="progress-bar step2">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Personalization</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Disclosures</p>
              </div>
            </div>
          </div>
          <div className="inner-content">
            <div className="heading">
              <p className="subtitle">Step 2</p>
              <h1 className="title">Personalization</h1>
              <p className="description">Select your strategy</p>
              <div className="score">
                <span className="number">325</span>
                <span className="text">Your Score</span>
              </div>
            </div>
            <div className="strategies-container">
              <div
                className={`strategy ${
                  data.strategy == "Hedgehog" ? "recommended" : ""
                }`}
              >
                <div className="strategy-content">
                  <div>
                    <img src="/assets/profiles/hedgehog.svg" alt="" />
                    <p className="strategy-title">Hedgehog</p>
                    <p className="strategy-description">
                      Focuses on minimizing risks and maximizing long-term
                      profits through diversification of the investment
                      portfolio, in order to minimize the negative effects of
                      market fluctuations.
                    </p>
                  </div>
                  <div>
                    <div className="strategy-checklist">
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Options</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Bonds</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">ETFs</span>
                      </div>
                    </div>
                    <div className="button-container">
                      <DgButton
                        category={"primary"}
                        size={"medium"}
                        text={"Choose"}
                        action={() => updateStrategy("Hedgehog")}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`strategy ${
                  data.strategy == "Bull" ? "recommended" : ""
                }`}
              >
                <div className="strategy-content">
                  <div>
                    <img src="/assets/profiles/bull-active.svg" alt="" />
                    <p className="strategy-title">Bull</p>
                    <p className="strategy-description">
                      Used by investors who expect prices to go up in the
                      future, in order to profit by selling at a higher price
                      than they bought.
                    </p>
                  </div>
                  <div>
                    <div className="strategy-checklist">
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Options</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Bonds</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">ETFs</span>
                      </div>
                    </div>
                    <div className="button-container">
                      <div className="button-gradient-wrapper">
                        <DgButton
                          category={"primary"}
                          size={"medium"}
                          text={"Choose"}
                          action={() => updateStrategy("Bull")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`strategy ${
                  data.strategy == "Bear" ? "recommended" : ""
                }`}
              >
                <div className="strategy-content">
                  <div>
                    <img src="/assets/profiles/bear.svg" alt="" />
                    <p className="strategy-title">Bear</p>
                    <p className="strategy-description">
                      Used by investors who expect prices to go down in the
                      future, in order to profit by selling at a higher price
                      than they bought.
                    </p>
                  </div>
                  <div>
                    <div className="strategy-checklist">
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Options</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">Bonds</span>
                      </div>
                      <div className="check">
                        <img
                          src="assets/check.png"
                          alt=""
                          className="check-img"
                        />
                        <span className="check-text">ETFs</span>
                      </div>
                    </div>
                    <div className="button-container">
                      <DgButton
                        category={"primary"}
                        size={"medium"}
                        text={"Choose"}
                        action={() => updateStrategy("Bear")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="button-container">
              <DgButton
                category={"primary"}
                size={"medium"}
                text={"Submit"}
                action={updateUser}
                loading={loading}
              />
            </div>
            <p className="info">
              Don't worry, you'll be able to change your strategy later in
              settings.
            </p>
          </div>
        </div>
      </BallsBackground>
    </div>
  );
}

export default StrategySelection;
